<script>
   /**************************************************************/
   /* ModelCalculator                                            */
   /* -------------------------                                  */
   /* component for training PCA model                           */
   /**************************************************************/

   import { pca_fit, pcamodel_get } from '../../../shared/wasm/dist/pca';
   import { app_pca_predict, genhash } from '../../../shared/utils.js';

   import Select from '../../../shared/controls/Select.svelte';
   import Container from '../../../shared/controls/Container.svelte';
   import Button from '../../../shared/controls/Button.svelte';
   import Spinner from '../../../shared/controls/Spinner.svelte';
   import ButtonCancel from '../../../shared/controls/ButtonCancel.svelte';
   import Range from '../../../shared/controls/Range.svelte';


   // main properties of the whole app
   export let WASM;
   export let model;
   export let calset;
   export let calres;
   export let outliers;

   // error handling parameetrs
   let status = 'neutral';
   let errMsg = null;

   // model calculation parameters
   let ncomp = 1;
   let center = 'yes';
   let scale = 'no';

   // promise for model calculation method
   let promise;

   // function that fits the model using given parameters
   async function computeModel(dataset, ncomp, center, scale) {
      promise = WASM()
         .then(Module => {
            status = 'computing';
            const m = pca_fit(Module, dataset.values, ncomp, center, scale, outliers);
            m['lim'] = {
               'classic': {
                  'h0': pcamodel_get(m, "ch0"),
                  'Nh': pcamodel_get(m, "cNh"),
                  'q0': pcamodel_get(m, "cq0"),
                  'Nq': pcamodel_get(m, "cNq"),
                  'g0': pcamodel_get(m, "cg0"),
                  'Ng': pcamodel_get(m, "cNg"),
                  'r0': pcamodel_get(m, "cr0"),
                  'Nr': pcamodel_get(m, "cNr")
               },
               'robust': {
                  'h0': pcamodel_get(m, "rh0"),
                  'Nh': pcamodel_get(m, "rNh"),
                  'q0': pcamodel_get(m, "rq0"),
                  'Nq': pcamodel_get(m, "rNq"),
                  'g0': pcamodel_get(m, "rg0"),
                  'Ng': pcamodel_get(m, "rNg"),
                  'r0': pcamodel_get(m, "rr0"),
                  'Nr': pcamodel_get(m, "rNr")
               }
            };
            m['hash'] = genhash();        // unique hash number to trace model updates
            m['varlabels'] = dataset.varlabels;            // array with variable text labels
            m['varindices'] = dataset.varindices;          // variable indices
            m['varvalues'] = dataset.varvalues;            // variable values if available (wavelength, etc.)
            m['varvaluesName'] = dataset.varvaluesName;    // title for variable axis
            m['varvaluesUnits'] = dataset.varvaluesUnits;  // units if variales are represented by numeric values, e.g. nm
            m['varrev'] = dataset.varrev                   // if variables must be reversed
            m['exclvars'] = [];                            // array with excluded variables

            m['prep'] = [];               // array with preprocessing methods — reserved for future use
            model = m;
            calres = app_pca_predict(Module, model, dataset, outliers);
            status = 'success';
         })
         .catch(e =>{
            model = null;
            calres = null;
            status = 'error';
            errMsg = e;
         });
   }

   // event handler which triggers the model computation
   function compute(e) {
      status = 'neutral';
      errMsg = null;
      computeModel(calset, ncomp, center === 'yes', scale === 'yes');
   }

   // function which resets the state of the component
   function reset() {
      status = 'neutral';
      promise = null;
      model = null;
      errMsg = null;
   }

   // if data file is empty reset status and error messages
   $: if (calset === null) reset();

   // set initial number of components depending on dataset size and manual limit of A = 20
   $: maxNComp = calset ? Math.min(calset.values.nrows - 2 - outliers.length, calset.values.ncols - 1) : null;
   $: ncomp = maxNComp ? Math.min(maxNComp, 20) : null;
</script>

{#if (calset && !model) && status !== 'computing'}
   <!-- settings for global model calculation -->
   <Container label="# of PCs">
      <Range id="ncomp" bind:value={ncomp} min={1} max={maxNComp} step={1} decNum={0} />
   </Container>
   <Container label="Center">
      <Select id="center" bind:value={center} options={["yes", "no"]} />
   </Container>
   <Container label="Scale">
      <Select id="scale" bind:value={scale} options={["yes", "no"]} />
   </Container>
   <Container label=" ">
      <Button on:click={compute}>Create model</Button>
   </Container>
{/if}


{#if promise}

   <!-- calculation is in process -->
   {#await promise}
      <Container>
         <Spinner />
         <p>Computing global model...</p>
      </Container>

   {:then value}
      <!-- status if succeed -->
      {#if model !== null}
      <Container>
         <p>Global model is computed: {model.ncomp} PCs</p>
         <ButtonCancel on:click={reset} />
      </Container>
      {/if}

   {:catch error}
      <!-- error message if failed -->
      <Container status="error">
         <p>{error}</p>
      </Container>
   {/await}

{/if}


<!-- error message -->
{#if status === 'error' || errMsg}
<Container status="error">
   <p>{errMsg}</p>
</Container>
{/if}
